import { Component, Input, OnInit, SkipSelf } from '@angular/core';
import { ControlContainer, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseDirective } from '@egym/ngx-adm-int';
import { Gym } from '../../../model/gym';
import { GymFinderService } from '../../../service/gymfinder.service';

@Component({
  selector: 'gymfinder-transactional-emails',
  templateUrl: './transactional-emails.component.html',
  styleUrls: ['./transactional-emails.component.scss'],
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (controlContainer: ControlContainer) => controlContainer,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
  standalone: true,
})
export class TransactionalEmailsComponent
  extends BaseDirective<string[]>
  implements OnInit
{
  @Input()
  gym: Gym;

  @Input()
  editMode: boolean;

  emailSendingTransactions: string[] = [];

  transactionLoadFailed = false;

  unavailableConfiguredTransactionNames: string[] = [];

  private availableTransactionNames?: Set<string>;

  loading = false;

  constructor(
    snackBar: MatSnackBar,
    private readonly gymFinderService: GymFinderService
  ) {
    super(snackBar);
  }

  ngOnInit(): void {
    this.loadEmailSendingTransactions();
  }

  get transactionalEmailConfigurationStatus(): string {
    return this.isSavingBlocked ? '' : 'valid';
  }

  get isSavingBlocked(): boolean {
    return (
      (this.loading && this.hasConfiguredRecipients()) ||
      (this.transactionLoadFailed && this.hasConfiguredRecipients()) ||
      this.unavailableConfiguredTransactionNames.length > 0
    );
  }

  addRecipient(transactionName: string): void {
    this.ensureRecipients(transactionName).push('');
  }

  removeRecipient(transactionName: string, index: number): void {
    const recipients = this.getRecipients(transactionName);
    recipients.splice(index, 1);

    if (recipients.length === 0) {
      delete this.gym.transactionalEmailsConfiguration
        ?.transactionalEmailRecipients[transactionName];
    }

    this.removeEmptyConfiguration();
  }

  setRecipient(transactionName: string, index: number, value: string): void {
    this.ensureRecipients(transactionName)[index] = value;
  }

  getRecipients(transactionName: string): string[] {
    return (
      this.gym.transactionalEmailsConfiguration?.transactionalEmailRecipients[
        transactionName
      ] ?? []
    );
  }

  private loadEmailSendingTransactions(): void {
    this.loading = true;
    this.transactionLoadFailed = false;
    this.gymFinderService.getEmailSendingTransactions().subscribe({
      next: transactions => {
        this.emailSendingTransactions = transactions;
        this.availableTransactionNames = new Set(transactions);
        this.removeEmptyConfiguration();
        this.loading = false;
      },
      error: () => {
        this.transactionLoadFailed = true;
        this.loading = false;
        this.openSnackBar('Error while getting transactional email options');
      },
    });
  }

  private ensureRecipients(transactionName: string): string[] {
    this.gym.transactionalEmailsConfiguration ??= {
      transactionalEmailRecipients: {},
    };
    this.gym.transactionalEmailsConfiguration.transactionalEmailRecipients ??=
      {};
    this.gym.transactionalEmailsConfiguration.transactionalEmailRecipients[
      transactionName
    ] ??= [];

    return this.gym.transactionalEmailsConfiguration
      .transactionalEmailRecipients[transactionName];
  }

  private removeEmptyConfiguration(): void {
    const configuration = this.gym.transactionalEmailsConfiguration;
    if (!configuration?.transactionalEmailRecipients) {
      this.gym.transactionalEmailsConfiguration = null;
      return;
    }

    for (const [transactionName, recipients] of Object.entries(
      configuration.transactionalEmailRecipients
    )) {
      configuration.transactionalEmailRecipients[transactionName] = Array.from(
        recipients ?? []
      );

      if (
        configuration.transactionalEmailRecipients[transactionName].length === 0
      ) {
        delete configuration.transactionalEmailRecipients[transactionName];
      }
    }

    if (Object.keys(configuration.transactionalEmailRecipients).length === 0) {
      this.gym.transactionalEmailsConfiguration = null;
    }

    this.updateUnavailableConfiguredTransactionNames();
  }

  private updateUnavailableConfiguredTransactionNames(): void {
    if (!this.availableTransactionNames) {
      this.unavailableConfiguredTransactionNames = [];
      return;
    }

    const transactionalEmailRecipients =
      this.gym.transactionalEmailsConfiguration?.transactionalEmailRecipients;

    this.unavailableConfiguredTransactionNames = Object.keys(
      transactionalEmailRecipients ?? {}
    ).filter(
      transactionName => !this.availableTransactionNames?.has(transactionName)
    );
  }

  private hasConfiguredRecipients(): boolean {
    const transactionalEmailRecipients =
      this.gym.transactionalEmailsConfiguration?.transactionalEmailRecipients;

    return Object.values(transactionalEmailRecipients ?? {}).some(
      recipients => (recipients ?? []).length > 0
    );
  }
}
